/**
 * @file InvoiceMenu.tsx
 * @brief Invoice as a ⋯-style menu anchored to its trigger, the same shape as
 *        the session "Paid" cost menu, with Download PDF / Copy reference.
 *
 * @details
 * DES 04-10-2026: the invoice preview was a full right-hand sheet (first the
 * paper iframe, then a digital card). DES asked for it to open like the
 * "Paid" pill menu, buttons included. The paper layout is only the PDF.
 *
 * Fields come from `GET /api/invoices/:id/html` (the render the PDF is built
 * from), read by `parseInvoiceHtml`, so the menu and the PDF cannot disagree
 * on an amount. Fetched only while the menu is open.
 *
 * Usage: wrap the trigger; pass `open`/`onOpenChange` to open it from
 * elsewhere too (an invoice table opens a row's menu on row click).
 *
 * @references
 * - features/sessions/SessionDetailPage.tsx ("Session cost" Paid menu)
 * - components/feedback/invoiceHtml.ts (parser)
 * - BACKEND/src/features/invoices/routes.js (GET /:id/html, GET /:id/pdf)
 *
 * @author DES
 * @date 04-10-2026
 */

import { useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Copy, Download, LoaderCircle } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { Skeleton } from "@/components/ui/Skeleton";
import { ApiClientError, apiClient } from "@/shared/api/client";
import { invoiceEndpoints } from "@/shared/api/endpoints";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import {
  formatMoney,
  parseInvoiceHtml,
  splitMoney,
  timeRange,
} from "./invoiceHtml";

interface InvoiceMenuProps {
  /** Invoice id (= session / meeting / note id). */
  invoiceId: string;
  /** The trigger button. */
  children: ReactNode;
  /** `paid` / `open` → the pill in the header, like the Paid menu. */
  status?: string | null;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  align?: "start" | "center" | "end";
  side?: "top" | "bottom";
  /** Gap from the trigger. Opened from a floating dock, keep it clear of the
   *  dock's glass (DES 04-10-2026: "too close to dock na ho"). */
  sideOffset?: number;
}

const STATUS_TEXT: Record<string, string> = {
  paid: "text-[#2E7D32] dark:text-[#86EFAC]",
  open: "text-amber-600 dark:text-amber-400",
};

function useInvoiceHtml(invoiceId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["invoice-html", invoiceId],
    enabled,
    queryFn: async () => {
      const res = await apiClient.get<string>(
        `${invoiceEndpoints.byId(invoiceId)}/html`,
        {
          responseType: "text",
          headers: { Accept: "text/html, text/plain" },
          transformResponse: [(data: string) => data],
        },
      );
      const body = res.data ?? "";
      if (body.trimStart().startsWith("{")) {
        let message = "Couldn't load invoice";
        try {
          const parsed = JSON.parse(body) as { error?: string; message?: string };
          message = parsed.error ?? parsed.message ?? message;
        } catch {
          /* not JSON after all */
        }
        throw new ApiClientError(message, res.status);
      }
      return body;
    },
    staleTime: 60_000,
  });
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1 text-[13px]">
      <span className={cn("shrink-0", strong ? "font-semibold text-app-text" : "text-muted-foreground")}>
        {label}
      </span>
      <span
        className={cn(
          "min-w-0 text-right tabular-nums text-app-text [overflow-wrap:anywhere]",
          strong && "font-semibold",
        )}
      >
        {value}
      </span>
    </div>
  );
}

export function InvoiceMenu({
  invoiceId,
  children,
  status = null,
  open: openProp,
  onOpenChange,
  align = "start",
  side,
  sideOffset = 8,
}: InvoiceMenuProps) {
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = (next: boolean) => {
    if (openProp === undefined) setOpenState(next);
    onOpenChange?.(next);
  };

  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);
  const html = useInvoiceHtml(invoiceId, open);
  const invoice = useMemo(
    () => (html.data ? parseInvoiceHtml(html.data) : null),
    [html.data],
  );

  const detail = (key: string) =>
    invoice?.details.find(([k]) => k.toLowerCase() === key)?.[1];
  const [product, refId] = (detail("reference") ?? "").split(" · ");
  const duration = detail("duration");
  const languages = detail("languages");
  const when = timeRange(detail("started"), detail("ended"));
  const [symbol, amount] = splitMoney(invoice?.total?.[1] ?? "");

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await apiClient.get(`${invoiceEndpoints.byId(invoiceId)}/pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice_${invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Download failed",
        description: err instanceof ApiClientError ? err.message : "Please try again.",
      });
    } finally {
      setDownloading(false);
    }
  };

  const copyRef = async () => {
    try {
      await navigator.clipboard.writeText(refId || invoiceId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked */
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        side={side}
        sideOffset={sideOffset}
        className="w-80 p-4"
      >
        <div className="flex items-center justify-between gap-2">
          <span className="text-[13px] font-semibold text-app-text-secondary">Invoice</span>
          {status ? (
            <span
              className={cn(
                "text-xs font-semibold capitalize",
                STATUS_TEXT[status] ?? "text-app-text-secondary",
              )}
            >
              {status}
            </span>
          ) : null}
        </div>

        {html.isLoading ? (
          <div className="mt-2 space-y-3">
            <Skeleton className="h-10 w-32" />
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : invoice ? (
          <>
            <p className="mt-1 text-4xl font-bold tracking-[-0.8px]">
              <span className="text-brand">{symbol}</span>
              <span className="text-app-text">{amount}</span>
            </p>
            <p className="mt-1 text-[13px] text-muted-foreground">
              {[product, duration].filter(Boolean).join(" · ")}
            </p>
            {when ? <p className="text-[13px] text-muted-foreground">{when}</p> : null}

            <div className="mt-4">
              {invoice.billTo[0] ? <Row label="Billed to" value={invoice.billTo[0]} /> : null}
              {languages && languages !== "—" ? <Row label="Languages" value={languages} /> : null}
              {invoice.amounts.map(([k, v]) => (
                <Row key={k} label={k} value={formatMoney(v)} />
              ))}
              {invoice.total ? (
                <Row strong label={invoice.total[0]} value={formatMoney(invoice.total[1])} />
              ) : null}
            </div>
          </>
        ) : (
          <p className="mt-2 text-[13px] text-muted-foreground">
            {html.error instanceof ApiClientError
              ? html.error.message
              : "Couldn't show this invoice here. The PDF has every detail."}
          </p>
        )}

        <DropdownMenuSeparator className="my-3" />
        <DropdownMenuItem
          disabled={downloading}
          onSelect={(e) => {
            e.preventDefault();
            void downloadPdf();
          }}
        >
          {downloading ? (
            <LoaderCircle className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          {downloading ? "Preparing…" : "Download PDF"}
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            void copyRef();
          }}
        >
          {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
          {copied ? "Copied" : "Copy reference"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
